if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');

        const debouncedOnChange = debounce((event) => {
          this.allowZeroQuantityInput();

          const value = parseInt(event.target.value, 10) || 0;
          const variantId = event.target.dataset.index || this.dataset.index;

          // 一覧側の0は「カートから削除」として扱う。
          // カートドロワー側ではなく、quick-add-bulk内の数量欄だけ0を許可する。
          if (value <= 0) {
            this.setQuantityValue(0);

            if (variantId) {
              this.updateMultipleQty(
                {
                  [variantId]: 0,
                },
                {
                  silent: true,
                  action: 'remove',
                }
              );
            } else {
              this.setRequestStarted(false);
            }

            return;
          }

          this.validateQuantity(event);
        }, ON_CHANGE_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.allowZeroQuantityInput();
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
      }

      connectedCallback() {
        this.allowZeroQuantityInput();
        this.listenForActiveInput();
        this.listenForKeydown();

        // cart.js / cart-drawer.js 側から publish されたカート更新を受け取り、
        // 一覧側の数量だけ同期する。
        // セクションHTMLの再取得はしないので高速。
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (!event || !event.cartData || !Array.isArray(event.cartData.items)) return;

            this.syncQuantityWithCart(event.cartData);
          });
        }
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      get input() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        this.allowZeroQuantityInput();

        if (!this.classList.contains('hidden')) {
          this.input?.addEventListener('focusin', (event) => {
            this.allowZeroQuantityInput();
            event.target.select();
          });
        }

        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.input?.addEventListener('keydown', (event) => {
          this.allowZeroQuantityInput();

          if (event.key === 'Enter') {
            this.input?.blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
            event.target.removeAttribute('aria-invalid');
          },
          { once: true }
        );
      }

      get sectionId() {
        if (!this._sectionId) {
          this._sectionId = this.closest('.collection-quick-add-bulk')?.dataset.id;
        }

        return this._sectionId;
      }

      getCartUpdateUrl() {
        return window.routes?.cart_update_url || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
      }

      dispatchQuickAddEvent(name, detail = {}) {
        window.dispatchEvent(
          new CustomEvent(`collection-quick-add:${name}`, {
            detail: {
              source: 'quick-add-bulk',
              variantId: this.dataset.index,
              ...detail,
            },
          })
        );
      }

      normalizeErrorMessage(error) {
        if (!error) return 'カートを更新できませんでした。もう一度お試しください。';
        if (typeof error === 'string') return error;
        if (error.description) return error.description;
        if (error.message) return error.message;
        if (error.errors) return typeof error.errors === 'string' ? error.errors : JSON.stringify(error.errors);
        return 'カートを更新できませんでした。もう一度お試しください。';
      }

      showInputError(message) {
        const input = this.input;
        if (!input) return;

        input.setCustomValidity(message);
        input.reportValidity();
        this.cleanErrorMessageOnType({ target: input });
      }

      updateCartBubble(itemCount) {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon || typeof itemCount !== 'number') return;

        let bubble = cartIcon.querySelector('.cart-count-bubble');

        if (itemCount <= 0) {
          bubble?.remove();
          cartIcon.dataset.cartCount = '0';
          return;
        }

        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartIcon.appendChild(bubble);
        }

        const visibleCount = itemCount < 100 ? String(itemCount) : '99+';

        bubble.innerHTML = `
          <span aria-hidden="true">${visibleCount}</span>
          <span class="visually-hidden">${itemCount}個の商品</span>
        `;

        cartIcon.dataset.cartCount = String(itemCount);
      }

      allowZeroQuantityInput() {
        const wrappers = new Set();
        wrappers.add(this);

        const variantId = String(this.dataset.index || '');

        if (variantId) {
          document.querySelectorAll(`quick-add-bulk[data-index="${variantId}"]`).forEach((element) => {
            wrappers.add(element);
          });

          if (this.sectionId) {
            const sectionElement = document.getElementById(`quick-add-bulk-${variantId}-${this.sectionId}`);
            if (sectionElement) wrappers.add(sectionElement);
          }
        }

        wrappers.forEach((wrapper) => {
          const inputs = wrapper.querySelectorAll('quantity-input input, input[type="number"]');

          inputs.forEach((input) => {
            input.min = '0';
            input.setAttribute('min', '0');

            if (input.dataset) {
              input.dataset.min = '0';
            }

            input.setCustomValidity('');
            input.removeAttribute('aria-invalid');
          });
        });
      }

      getVariantQuantityFromCart(cart) {
        const variantId = parseInt(this.dataset.index, 10);
        if (!variantId || !cart || !Array.isArray(cart.items)) return 0;

        return cart.items.reduce((total, item) => {
          const itemVariantId = parseInt(item.id || item.variant_id, 10);
          if (itemVariantId !== variantId) return total;

          return total + (parseInt(item.quantity, 10) || 0);
        }, 0);
      }

      syncQuantityWithCart(cart) {
        const quantity = this.getVariantQuantityFromCart(cart);
        this.setQuantityValue(quantity);
      }

      setQuantityValue(quantity) {
        const normalizedQuantity = Math.max(0, parseInt(quantity, 10) || 0);
        const value = String(normalizedQuantity);

        const wrappers = new Set();
        wrappers.add(this);

        const variantId = String(this.dataset.index || '');

        if (variantId) {
          document.querySelectorAll(`quick-add-bulk[data-index="${variantId}"]`).forEach((element) => {
            wrappers.add(element);
          });

          if (this.sectionId) {
            const sectionElement = document.getElementById(`quick-add-bulk-${variantId}-${this.sectionId}`);
            if (sectionElement) wrappers.add(sectionElement);
          }
        }

        wrappers.forEach((wrapper) => {
          const inputs = wrapper.querySelectorAll('quantity-input input, input[type="number"]');

          inputs.forEach((input) => {
            input.min = '0';
            input.setAttribute('min', '0');

            if (input.dataset) {
              input.dataset.min = '0';
            }

            input.value = value;
            input.setAttribute('value', value);
            input.defaultValue = value;
            input.dataset.cartQuantity = value;
            input.setCustomValidity('');
            input.removeAttribute('aria-invalid');

            input.dispatchEvent(new Event('input', { bubbles: true }));
          });

          const minusButtons = wrapper.querySelectorAll('button[name="minus"]');

          minusButtons.forEach((button) => {
            if (normalizedQuantity <= 0) {
              button.disabled = true;
              button.setAttribute('disabled', 'disabled');
            } else {
              button.disabled = false;
              button.removeAttribute('disabled');
            }
          });
        });
      }

      getItemsToUpdate(items) {
        const updates = {};

        Object.entries(items).forEach(([id, quantity]) => {
          const parsedId = parseInt(id, 10);
          const parsedQuantity = Math.max(0, parseInt(quantity, 10) || 0);

          if (!Number.isFinite(parsedId) || parsedId <= 0) return;

          updates[String(parsedId)] = parsedQuantity;
        });

        return updates;
      }

      updateMultipleQty(items, options = {}) {
        const updates = this.getItemsToUpdate(items);
        const progressBar = this.selectProgressBar();

        if (Object.keys(updates).length === 0) {
          this.setRequestStarted(false);
          return;
        }

        const isRemoving = Object.values(updates).some((quantity) => parseInt(quantity, 10) === 0);
        const action = options.action || (isRemoving ? 'remove' : 'update');
        const silent = Boolean(options.silent);

        progressBar?.classList.remove('hidden');

        // 削除時は通知パネルを出さない。
        // 「カートに追加しました」と表示される事故を防ぐため。
        if (!silent) {
          this.dispatchQuickAddEvent('request-start', {
            updates,
            action,
          });
        }

        const body = JSON.stringify({
          updates,
        });

        fetch(this.getCartUpdateUrl(), {
          ...fetchConfig(),
          body,
        })
          .then((response) => {
            return response.json().then((state) => {
              if (!response.ok) throw state;
              return state;
            });
          })
          .then((cart) => {
            this.updateCartBubble(cart.item_count);
            this.syncQuantityWithCart(cart);

            if (!silent) {
              this.dispatchQuickAddEvent('request-success', {
                cartData: cart,
                updates,
                action,
              });
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'quick-add',
                cartData: {
                  ...cart,
                  variant_id: parseInt(this.dataset.index, 10),
                },
              });
            }
          })
          .catch((error) => {
            const message = this.normalizeErrorMessage(error);
            console.error('[quick-add-bulk] cart update failed', error);

            this.showInputError(message);

            if (!silent) {
              this.dispatchQuickAddEvent('request-error', {
                error,
                message,
                action,
              });
            }
          })
          .finally(() => {
            progressBar?.classList.add('hidden');
            this.setRequestStarted(false);
          });
      }
    }
  );
}